import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  AnalyticsReportResultsQuery,
  AnalyticsSnapshotResultsQuery,
  AnalyticsTraceabilityResultsQuery,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

export type OneRecord = Record<string, unknown>

type TableVisualization = Extract<
  React.ComponentProps<typeof OneDataCollection>['visualizations'][number],
  { type: 'table' }
>

export type ColumnFormatHint = {
  column_alias: string
  column_format: string
}

const getColumnType = (value: string, columnTypeHint?: string) => {
  if (columnTypeHint) {
    return columnTypeHint
  }

  // Try to infer the column type from the value
  if (value === '') {
    return 'text'
  }
  if (!Number.isNaN(+String(value))) {
    return 'number'
  }
  if (value === 'true' || value === 'false') {
    return 'boolean'
  }
  // Only process as date if value contains numbers, '-' or 'o'
  const dateCandidateRegex = /[\d\-o]/
  if (dateCandidateRegex.test(value)) {
    const date = new Date(value)
    if (date.toString() !== 'Invalid Date') {
      return 'date'
    }
  }

  const framents = value.split(',')
  // Estimate an average tag length of 15 characters if greater it can not be a tag list
  if (framents.length > 1 && framents.length * 15) {
    return 'tagList'
  }
  return 'text'
}

type Cell = {
  value: string
} & Record<string, unknown>

export const formatColumns = (
  columnNodes:
    | AnalyticsReportResultsQuery['iris']['sqlResultsConnection']['nodes']
    | AnalyticsSnapshotResultsQuery['iris']['snapshotResultsConnection']['nodes']
    | AnalyticsTraceabilityResultsQuery['iris']['traceabilityResultsConnection']['nodes'],
  columnFormatHints: ColumnFormatHint[],
  locale: string
): TableVisualization['options']['columns'] => {
  return (
    columnNodes[0]?.values.map((result) => {
      const columnTypeHint =
        result.formatHint ??
        columnFormatHints.find((hint) => hint.column_alias === result.name)?.column_format
      const currencyColumn =
        columnNodes[0]?.values.find((column) => column.formatHint === 'monetary_currency')?.name ??
        columnFormatHints.find((hint) => hint.column_format === 'monetary_currency')?.column_alias

      return {
        label: result.displayName ?? result.name ?? '',
        align: columnTypeHint === 'monetary_amount' ? 'right' : 'left',
        render: (item: OneRecord) => {
          const columnType = getColumnType(String(item[result.name]), columnTypeHint)

          const cell: Cell = {
            value: String(item[result.name]),
            ...(columnType === 'monetary_amount' &&
              currencyColumn && { currency: String(item[currencyColumn]) }),
          }

          const renderers = {
            monetary_amount: (cell: Cell) => {
              return {
                type: 'text' as const,
                value: formattedAmount({
                  amountCents: Number(cell.value) * 100,
                  currency: String(cell.currency),
                  locale: locale,
                }),
              }
            },
            number: (cell: Cell) => ({
              type: 'number' as const,
              value: Number(cell.value),
            }),
            date: (cell: Cell) => ({
              type: 'date' as const,
              value: new Date(String(cell.value)),
            }),
            text: (cell: Cell) => ({
              type: 'text' as const,
              value: String(cell.value),
            }),
            boolean: (cell: Cell) => ({
              type: 'dotTag' as const,
              value: {
                label:
                  String(cell.value) === 'true'
                    ? i18n.t('ai_reports.boolean.true')
                    : i18n.t('ai_reports.boolean.false'),
                color: String(cell.value) === 'true' ? 'viridian' : 'radical',
              },
            }),
            tagList: (cell: Cell) => ({
              type: 'tagList' as const,
              value: {
                type: 'raw' as const,
                tags: String(cell.value)
                  .split(',')
                  .filter((tag) => tag.trim() !== '')
                  .map((tag) => ({
                    text: tag,
                  })),
              },
            }),
            array: (cell: Cell) => ({
              type: 'tagList' as const,
              value: {
                type: 'raw' as const,
                tags: JSON.parse(String(cell.value))
                  .filter((tag: string) => tag.trim() !== '')
                  .map((item: string) => ({
                    text: item,
                  })),
              },
            }),
          } satisfies Record<string, (cell: Cell) => unknown>

          const isKnownRenderer = (type: string): type is keyof typeof renderers => {
            return type in renderers
          }
          const renderer = isKnownRenderer(columnType) ? renderers[columnType] : renderers.text

          return (
            renderer?.(cell) ?? {
              type: 'text',
              value: String(cell.value),
            }
          )
        },
      }
    }) ?? []
  )
}

function parseDatasourceType(name: string) {
  switch (name) {
    case 'current':
    case 'snapshot':
    case 'traceability':
      return name
    default:
      throw new Error(`Unknown datasource type ${name}`)
  }
}

const PATTERN_DATASOURCE_TYPE = /.*_(current|snapshot|traceability)$/
export function datasourceTypeFromId(id: string) {
  const match = PATTERN_DATASOURCE_TYPE.exec(id)
  if (!match) throw new Error(`Cannot parse type from datasource ID ${id}`)
  const type = match[1]
  if (!type)
    throw new Error(`Error parsing type from datasource ID ${id} - ${JSON.stringify(match)}`)

  return parseDatasourceType(type)
}
